import { useCallback, useEffect, useRef, useState } from 'react';
import type { AskCard, ChatAttachment, ChatContext, PageRef, PendingPage, StatusLine } from '@shared/api';
import type { Decision } from '@shared/types';
import { skipAsk, stopChat, streamChat } from '../api/client';
import { activeContextLabel, queuedContextLabel, type QueuedChatRequest } from './chatQueue';

/**
 * The conversation is a transcript of what happened, not the source of truth —
 * the proposal object is. This hook only holds what was said and which tools
 * ran, and tells the caller when the server reports a new proposal version so
 * the real state can be refetched.
 */
export type AssistantPart =
  | { kind: 'text'; text: string }
  | { kind: 'tool'; name: string }
  | { kind: 'ask'; ask: AskCard };

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  /** User messages carry plain text. */
  text: string;
  /** Assistant messages carry interleaved text, tool-status and ask parts. */
  parts: AssistantPart[];
  streaming: boolean;
  error: string | null;
  /** Short label describing the context attached to a user message. */
  contextLabel: string | null;
  /**
   * The page the rep was viewing when this message was sent (user messages
   * only, null otherwise) — resolved by ProposalView from the document's
   * visible sections and passed through `send()`. This is the page chip's
   * fallback source (message-bar §2): the running/waiting/done row shows
   * this until a `status` event names one instead.
   */
  page: PageRef | null;
  /**
   * The proposal version the sender was looking at when this message was
   * sent (user messages only, null otherwise). Compared against the
   * following assistant message's `version` to decide whether a done row
   * shows Undo (docs/composer-cards.md 5c).
   */
  versionAtSend: number | null;
  /** The latest proposal version reported by `state`/`done` for this turn (assistant messages only). */
  version: number | null;
  /**
   * What the turn actually changed, from the `done` event (assistant
   * messages only, `[]` otherwise): the change summaries of every version
   * this turn saved, oldest first. Empty when nothing was saved. The done
   * row shows this as its primary content (docs/composer-cards.md 5a).
   */
  summary: string[];
  /**
   * The latest `status` event for this turn (assistant messages only, null
   * before the first one arrives). Kept after `done` rather than cleared —
   * the running row's label comes from `status.label` (falling back to the
   * old tool-name phrase until the first one lands), and message-bar §2
   * reads `status.page` off whichever one this holds.
   */
  status: StatusLine | null;
  /**
   * The most recent page any `status` event named this turn (assistant
   * messages only, null until one does) — sticky across statuses that carry
   * no page of their own, so it never regresses to "no page" mid-turn. The
   * page chip formula is `statusPage ?? ` the send's own `page` above
   * (message-bar §2: running switches to it, done keeps the last one named).
   */
  statusPage: PageRef | null;
  /** Set when this turn ended via POST .../chat/stop (docs/composer-cards.md 5i). */
  stopped?: boolean;
  /**
   * Client-only: Skip cleared this row (docs/composer-cards.md 4d, 11a).
   * Nothing is recorded server-side; the row is just no longer shown.
   */
  dismissed?: boolean;
  /**
   * Client-only: the done row's Undo reverted this turn back to this
   * version (docs/composer-cards.md 5c). Once set, the row swaps its text
   * for "Undone. Back to v{n}." and drops the Undo button so a second click
   * cannot revert again.
   */
  undoneToVersion?: number | null;
  /**
   * Client-only: the rep responded to this turn's question — tapped an
   * answer, typed a reply, or hit Skip — while the turn was still running
   * (a `keepWorking` ask, docs/decisions.md §6). The row goes back to
   * running with the question closed, and the ask never pauses the queue
   * (docs/composer-cards.md 10j): the reply is already queued behind the
   * turn and reaches the agent as the next message.
   */
  askSettled?: boolean;
}

export interface ChatStore {
  messages: ChatMessage[];
  /** Requests waiting for the current turn to finish, in order. Exposed as state so it can render. */
  queue: QueuedChatRequest[];
  sending: boolean;
  send: (
    message: string,
    context: ChatContext | undefined,
    contextLabel: string | null,
    versionAtSend?: number | null,
    page?: PageRef | null,
    /** simplify.md §6: carries a resolved decision's id through to the server (ComposerDock.tsx's `[decision] …` follow-up) — see QueuedChatRequest.decisionId. */
    decisionId?: string,
    attachments?: ChatAttachment[],
  ) => void;
  /** Aborts the running turn after its current tool and empties the queue (docs/composer-cards.md 10h, 10k). */
  stop: () => void;
  /** Drops one queued request (state 3, the ✕). */
  removeQueued: (userId: string) => void;
  /**
   * Skip. On a waiting row: clears the ask and starts the next queued
   * request. On a done row: clears the row. Client-only, records nothing.
   */
  dismiss: () => void;
  /** Marks a turn as undone back to `version` (docs/composer-cards.md 5c): the done row swaps its text and drops the Undo button. */
  markUndone: (messageId: string, version: number) => void;
  /** True once at least one turn has been sent in this session. */
  started: boolean;
}

let counter = 0;
const nextId = (): string => `m${++counter}`;

/**
 * The server may run more than one model turn per chat turn (a "no tool
 * call" nudge re-prompts the model once), and every turn's text is streamed
 * as `text` events — so the streamed parts can hold more than one turn's
 * text run together. `done.finalText` is the authoritative reply (the last
 * assistant turn's text only), so on `done` every streamed text part is
 * replaced with a single part holding it. `tool` and `ask` parts keep their
 * positions; the text part goes after the last of them (or is the only part
 * when there are none). An empty `finalText` drops the text part entirely.
 * Pure so it's directly testable without driving the hook.
 */
export function finalizeParts(parts: AssistantPart[], finalText: string): AssistantPart[] {
  const nonText = parts.filter((part) => part.kind !== 'text');
  return finalText ? [...nonText, { kind: 'text', text: finalText }] : nonText;
}

/** Mirrors src/agent/context.ts normalizeForAskMatch: trim, lowercase, collapse whitespace, strip trailing punctuation. */
function normalizeAskAnswer(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ').replace(/[.,!?;:]+$/, '');
}

/** True when `message` matches one of the ask's answer labels the same loose way the server does (their decision, not a fresh instruction). */
export function answersAsk(message: string, ask: AskCard): boolean {
  const normalized = normalizeAskAnswer(message);
  return ask.answers.some((answer) => normalizeAskAnswer(answer.label) === normalized);
}

/**
 * docs/composer-cards.md 10j: whether the question a turn leaves open
 * pauses the client queue when that turn ends. Only an unanswered,
 * direct-decision (`allowLater: false`) ask pauses it. An ask the rep
 * already settled — answered, replaced or skipped while the turn kept
 * working (`settled`) — or one a queued request answers by label, lets the
 * next request start, so a queued answer is never stranded behind the very
 * question it answers. Pure so the rule is directly testable.
 */
export function askPausesQueue(ask: AskCard | null, settled: boolean, queue: readonly QueuedChatRequest[]): boolean {
  if (!ask || settled || ask.allowLater) return false;
  return !queue.some((request) => answersAsk(request.message, ask));
}

export function useChat(
  proposalId: string | null,
  onVersion: (version: number) => void,
  /**
   * docs/decisions.md §5e/§8b: a `{kind:'decision'}` SSE event fires mid-turn,
   * before `state`/`done` — the join wrote a new ledger entry through
   * `ctx.onDecision` so the assumptions strip can update without waiting for
   * the turn to finish. No version rides along, so the caller decides how to
   * pick the change up (a refetch is the simplest correct answer).
   */
  onDecision?: (decision: Decision) => void,
  /** docs/custom-page.md "Pending page": forwarded straight through to usePendingPages. */
  onPagePending?: (pending: PendingPage) => void,
  onPageResolved?: (key: string, outcome: 'added' | 'not_added', sectionIds?: string[]) => void,
  /** Fired once per turn on `done` or `error` — the pending-page hook drops any leftover placeholder here. */
  onChatSettled?: () => void,
): ChatStore {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [queue, setQueue] = useState<QueuedChatRequest[]>([]);
  const [sending, setSending] = useState(false);
  const activeRef = useRef(false);
  const startRef = useRef<(request: QueuedChatRequest) => void>(() => undefined);
  const abortRef = useRef<AbortController | null>(null);
  const abortWanted = useRef(false);
  const versionCallback = useRef(onVersion);
  versionCallback.current = onVersion;
  const decisionCallback = useRef(onDecision);
  decisionCallback.current = onDecision;
  const pagePendingCallback = useRef(onPagePending);
  pagePendingCallback.current = onPagePending;
  const pageResolvedCallback = useRef(onPageResolved);
  pageResolvedCallback.current = onPageResolved;
  const chatSettledCallback = useRef(onChatSettled);
  chatSettledCallback.current = onChatSettled;
  // The assistant message id whose ask is still OPEN — shown and not yet
  // answered, replaced or skipped — or null. Reset at the start of every
  // turn; set when that turn asks; cleared by send() and dismiss() the moment
  // the rep responds, whether the turn has ended or is still running behind
  // a keepWorking question (docs/decisions.md §6).
  const waitingRef = useRef<string | null>(null);

  // A different proposal is a different conversation — the view is keyed by
  // proposal id, so this hook only has to hang up the stream on the way out.
  //
  // The abort is deferred by a tick and cancelled if the effect immediately
  // re-runs, so a remount (React StrictMode does one on every mount in dev)
  // does not kill a stream that is mid-flight.
  useEffect(() => {
    abortWanted.current = false;
    return () => {
      abortWanted.current = true;
      const controller = abortRef.current;
      window.setTimeout(() => {
        if (abortWanted.current) controller?.abort();
      }, 0);
    };
  }, [proposalId]);

  const start = useCallback((request: QueuedChatRequest) => {
      if (!proposalId) return;
      activeRef.current = true;
      waitingRef.current = null;
      // The question this turn showed, if any — read at turn end by the
      // queue rule (askPausesQueue, docs/composer-cards.md 10j).
      let openAsk: AskCard | null = null;
      const assistantId = nextId();
      setMessages((prev) => [
        ...prev.map((message) => message.id === request.userId ? { ...message, contextLabel: activeContextLabel(request) } : message),
        {
          id: assistantId,
          role: 'assistant',
          text: '',
          parts: [],
          streaming: true,
          error: null,
          contextLabel: null,
          page: null,
          versionAtSend: null,
          version: null,
          summary: [],
          status: null,
          statusPage: null,
        },
      ]);
      setSending(true);

      const patch = (fn: (msg: ChatMessage) => ChatMessage) =>
        setMessages((prev) => prev.map((m) => (m.id === assistantId ? fn(m) : m)));

      const appendText = (delta: string) =>
        patch((m) => {
          const parts = [...m.parts];
          const last = parts[parts.length - 1];
          if (last && last.kind === 'text') {
            parts[parts.length - 1] = { kind: 'text', text: last.text + delta };
          } else {
            parts.push({ kind: 'text', text: delta });
          }
          return { ...m, parts };
        });

      const controller = new AbortController();
      abortRef.current?.abort();
      abortRef.current = controller;

      void (async () => {
        try {
          for await (const event of streamChat(
            proposalId,
            {
              message: request.message,
              ...(request.attachments?.length ? { attachments: request.attachments } : {}),
              ...(request.context ? { context: request.context } : {}),
              ...(request.decisionId ? { decisionId: request.decisionId } : {}),
            },
            controller.signal,
          )) {
            switch (event.kind) {
              case 'text':
                appendText(event.delta);
                break;
              case 'tool':
                patch((m) => ({ ...m, parts: [...m.parts, { kind: 'tool', name: event.name }] }));
                break;
              case 'status':
                patch((m) => ({
                  ...m,
                  status: event.status,
                  // Sticky: a status with no page of its own doesn't erase
                  // the last one that did (message-bar §2).
                  statusPage: event.status.page ?? m.statusPage,
                }));
                break;
              case 'ask':
                // Arrives mid-turn: with `keepWorking` the stream keeps
                // going behind it and the row shows the question on top of
                // the running status (taskRows). Whether it gates the queue
                // is decided at turn end (askPausesQueue): an agent-raised
                // follow-up never does, and neither does a question the rep
                // has answered or skipped by then.
                openAsk = event.ask;
                waitingRef.current = assistantId;
                patch((m) => ({ ...m, parts: [...m.parts, { kind: 'ask', ask: event.ask }] }));
                break;
              case 'decision':
                decisionCallback.current?.(event.decision);
                break;
              case 'page_pending':
                pagePendingCallback.current?.(event.pending);
                break;
              case 'page_resolved':
                pageResolvedCallback.current?.(event.key, event.outcome, event.sectionIds);
                break;
              case 'state':
                versionCallback.current(event.version);
                patch((m) => ({ ...m, version: event.version }));
                break;
              case 'done':
                patch((m) => ({
                  ...m,
                  parts: finalizeParts(m.parts, event.finalText),
                  streaming: false,
                  version: event.version,
                  stopped: event.stopped,
                  summary: event.summary,
                }));
                versionCallback.current(event.version);
                chatSettledCallback.current?.();
                break;
              case 'error':
                patch((m) => ({ ...m, streaming: false, error: event.message }));
                chatSettledCallback.current?.();
                break;
            }
          }
        } finally {
          if (!controller.signal.aborted) {
            patch((m) => (m.streaming ? { ...m, streaming: false } : m));
            setSending(false);
            activeRef.current = false;
            // docs/composer-cards.md 10j: only an ask that is still open and
            // unanswered pauses the queue. `settled` — send() or dismiss()
            // already cleared waitingRef for this turn — covers a rep who
            // answered, replaced or skipped a keepWorking question while
            // the turn was still running; a queued request matching an
            // answer label covers the same thing by content. Everything
            // else (done, stopped, error, an allowLater ask) starts the
            // next queued request, if any.
            const settled = waitingRef.current !== assistantId;
            setQueue((prev) => {
              if (askPausesQueue(openAsk, settled, prev)) return prev;
              const [next, ...rest] = prev;
              if (next) window.setTimeout(() => startRef.current(next), 0);
              return rest;
            });
          }
        }
      })();
    }, [proposalId, setQueue]);
  startRef.current = start;

  const send = useCallback((
    message: string,
    context: ChatContext | undefined,
    contextLabel: string | null,
    versionAtSend: number | null = null,
    page: PageRef | null = null,
    decisionId?: string,
    attachments: ChatAttachment[] = [],
  ) => {
    const trimmed = message.trim();
    if (!proposalId || !trimmed) return;
    const request: QueuedChatRequest = {
      userId: nextId(),
      message: trimmed,
      context,
      contextLabel,
      page,
      ...(attachments.length ? { attachments } : {}),
      ...(decisionId ? { decisionId } : {}),
    };
    setMessages((prev) => [...prev, {
      id: request.userId,
      role: 'user',
      text: trimmed,
      parts: [],
      streaming: false,
      error: null,
      contextLabel: activeRef.current ? queuedContextLabel(contextLabel) : contextLabel,
      page,
      versionAtSend,
      version: null,
      summary: [],
      status: null,
      statusPage: null,
    }]);
    // Any send answers, or replaces, the open question (the server withdraws
    // a question any other message reaches it with — src/agent/context.ts).
    // Settle it client-side now: on a turn still running behind a
    // keepWorking ask, the row goes back to running with the question
    // closed and the reply queued behind it, and 10j never pauses the queue
    // at turn end behind a reply that is already in it.
    const waitingId = waitingRef.current;
    if (waitingId) {
      waitingRef.current = null;
      setMessages((prev) => prev.map((m) => (m.id === waitingId ? { ...m, askSettled: true } : m)));
    }
    // A turn is only "active" while one is literally streaming — a turn that
    // ended in a question already flipped this back to false, so answering
    // it (or sending anything else) starts right away instead of queueing
    // behind whatever else is already waiting (any send resumes, 10j).
    if (activeRef.current) {
      setQueue((prev) => [...prev, request]);
    } else {
      startRef.current(request);
    }
  }, [proposalId, setQueue]);

  const stop = useCallback(() => {
    if (proposalId) void stopChat(proposalId).catch(() => undefined);
    setQueue(() => []);
  }, [proposalId, setQueue]);

  const removeQueued = useCallback((userId: string) => {
    setQueue((prev) => prev.filter((request) => request.userId !== userId));
  }, [setQueue]);

  const dismiss = useCallback(() => {
    const waitingId = waitingRef.current;
    if (waitingId) {
      waitingRef.current = null;
      if (proposalId) void skipAsk(proposalId);
      if (activeRef.current) {
        // Skip on a keepWorking question while its turn is still running:
        // only the question goes — the row returns to running and the
        // queue waits for the turn to end as usual (askPausesQueue then
        // sees the ask settled and lets the next request start).
        setMessages((prev) => prev.map((m) => (m.id === waitingId ? { ...m, askSettled: true } : m)));
        return;
      }
      setMessages((prev) => prev.map((m) => (m.id === waitingId ? { ...m, dismissed: true } : m)));
      setQueue((prev) => {
        const [next, ...rest] = prev;
        if (next) window.setTimeout(() => startRef.current(next), 0);
        return rest;
      });
    } else {
      setMessages((prev) => {
        let lastAssistant = -1;
        for (let i = prev.length - 1; i >= 0; i -= 1) {
          if (prev[i]?.role === 'assistant') { lastAssistant = i; break; }
        }
        if (lastAssistant === -1) return prev;
        const target = prev[lastAssistant];
        if (!target) return prev;
        const next = [...prev];
        next[lastAssistant] = { ...target, dismissed: true };
        return next;
      });
    }
  }, [proposalId, setQueue]);

  const markUndone = useCallback((messageId: string, version: number) => {
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, undoneToVersion: version } : m)));
  }, []);

  return { messages, queue, sending, send, stop, removeQueued, dismiss, markUndone, started: messages.length > 0 };
}
